import { EventType } from "./constants/event-type-constants";
import { DOMHelper } from "./helpers/dom-helper";
import { BigData } from "./marketing-client-bigdata";
import { BigDataHelper } from "./helpers/big-data-helper";
import { DeviceInformationHelper, DeviceType } from "./helpers/device-information-helper";
import { WYBORCZA_PL } from "./marketing-client";
import { Capping } from "./marketing-client-capping";
import { Utilization } from "./marketing-client-utilization";
import { BigDataEventConstants } from "./constants/big-data-constants";
import { CollectionIdsObject, CtaObject, Utils } from "./utils/utils";
import { MarketingClientConstants } from "./constants/marketing-client-constants";
import {
  ObjectConstants,
  ErrorMessage,
  Selector,
  HtmlElements,
  ProhibitedDomains
} from "./constants/one-day-pass-old-constants";
import { CtaObjectConstants } from "./constants/cta-object-constants";
import { UtilizationConstants } from "./constants/marketing-client-utilization-constants";

export class OneDayPassOld {
  loader: any;
  token: string;
  module: HTMLElement;
  collectionIdsObject: CollectionIdsObject;
  form: HTMLFormElement;
  isMobile: Boolean;

  constructor(public capping: Capping, public utilizationObject: Utilization) {
    this.isMobile = DeviceInformationHelper.getDeviceType() === DeviceType.MOBILE;
  }
  ctaObject: CtaObject;
  tokenIframe: HTMLIFrameElement | null;

  init() {
    this.module = DOMHelper.querySelector(Selector.closeActivation) as HTMLElement;
    this.form = DOMHelper.querySelector(`${Selector.closeActivation} ${HtmlElements.form}`) as HTMLFormElement;
    this.tokenIframe = DOMHelper.querySelectorAsArray(HtmlElements.iframe)[0] as HTMLIFrameElement;

    this.ctaObject = Utils.prepareCtaObject(this.module);
    this.collectionIdsObject = Utils.prepareCollectionIdsObject(this.module);

    if (!this.module) {
      return;
    }

    this.hideEmailField();
    this.bindActionsForLoginAndLogout();
    this.getTokenAndListenIframe();
    this.bindSubmitForm();
  }

  hideEmailField() {
    if (WYBORCZA_PL?.userInfo?.loggedIn) {
      this.hideLogoutElements();
      this.showLoginElements();
      this.setLoginChannel();
      this.setUserEmail();
    } else {
      this.hideLoginElements();
    }
  }

  hideLogoutElements() {
    const elements = this.module ? DOMHelper.querySelectorAsArray(Selector.isLogout) : [];
    elements.forEach((element) => element.classList.add(MarketingClientConstants.hiddenClassName));

    const footerElements = DOMHelper.querySelectorAsArray(`${Selector.footer} ${Selector.loggedHide}`);
    footerElements.forEach((element) => element.classList.add(MarketingClientConstants.hiddenClassName));
  }

  showLoginElements() {
    const isLoginElements = this.module ? DOMHelper.querySelectorAsArray(Selector.isLogin) : [];
    isLoginElements.forEach((element) => element.classList.remove(MarketingClientConstants.hiddenClassName));
  }

  setLoginChannel() {
    const loginChannelElements = this.module ? DOMHelper.querySelectorAsArray(Selector.loginChannel) : [];
    loginChannelElements.forEach((element) => element.classList.add(WYBORCZA_PL?.userInfo?.channel));
  }

  setUserEmail() {
    const userEmailElements = this.module ? DOMHelper.querySelectorAsArray(Selector.userEmail) : [];
    userEmailElements.forEach((element) => (element.textContent = WYBORCZA_PL?.userInfo?.email));
  }

  hideLoginElements() {
    const isLoginElements = this.module ? DOMHelper.querySelectorAsArray(Selector.isLogin) : [];
    isLoginElements.forEach((element) => element.classList.add(MarketingClientConstants.hiddenClassName));
  }

  bindActionsForLoginAndLogout() {
    const loginBtn = DOMHelper.querySelectorAsArray(`${Selector.closeActivation} ${Selector.loginAction}`) as Element[];
    const logoutBtn = DOMHelper.querySelector(`${Selector.closeActivation} ${Selector.logoutAction}`) as HTMLElement;

    loginBtn?.forEach((btn) => {
      btn.addEventListener(EventType.CLICK, () => {
        this.goToLogin();
      });
    });

    if (logoutBtn) {
      logoutBtn?.addEventListener(EventType.CLICK, (e) => {
        this.logout(e);
      });
    }
  }

  logout(e: Event) {
    e.preventDefault();
    let ssoCookieValue = (document.cookie.match(ObjectConstants.ssoSessionCookieName + "=([^;]+)") || [])[1] || "";
    let url = ObjectConstants.locationLogin + "/services/logout/jsonp";

    if (location.host.indexOf(Selector.rootHostname) === -1) {
      url += "?SsoSessionPermanent=" + ssoCookieValue;
    }

    const script = document.createElement("script");
    const callbackName = "logoutCallback";
    window[callbackName] = this.logoutCallback.bind(this);
    script.src = url + `?callback=${callbackName}`;
    document.body.appendChild(script);
  }

  logoutCallback() {
    document.cookie = `${ObjectConstants.cookieName}=; expires=${new Date(0).toUTCString()}; path=/;`;
    document.location.href = ObjectConstants.visitedUrl;
  }

  goToLogin(): void {
    window.location.href = ObjectConstants.locationLogin;
  }

  getTokenAndListenIframe() {
    if (this.tokenIframe?.contentDocument) {
      if (this.tokenIframe?.contentDocument.readyState === "complete") {
        this.getToken(ObjectConstants.tokenUrl);
      }
    } else {
      this.tokenIframe?.addEventListener("load", (event: Event) => {
        this.getToken(ObjectConstants.tokenUrl);
      });
    }
  }

  bindSubmitForm() {
    this.form?.addEventListener(EventType.SUBMIT, (event) => {
      if (this.token) {
        this.validRequiredCondition(this.form);
        this.validOneClickCondition(this.form);
        if (!WYBORCZA_PL?.userInfo?.loggedIn) {
          this.validEmail(this.form);
        } else {
          this.formSubmit();
        }
      }
      event.preventDefault();
    });
  }

  async validEmail(form: HTMLFormElement) {
    const emailInput = form.querySelector(ObjectConstants.emailInput) as HTMLInputElement;
    const emailValue = emailInput?.value;
    const errors = {
      emailUsed: ErrorMessage.used,
      invalidEmail: ErrorMessage.invalid,
      prohibitedDomain: ErrorMessage.prohibitedDomain
    };
    emailInput?.classList.remove(ObjectConstants.error);

    if (!this.isValidEmail(emailValue)) {
      this.displayEmailError(emailInput, errors.invalidEmail);
      return false;
    }

    if (this.isProhibitedDomain(emailValue)) {
      this.displayEmailError(emailInput, errors.prohibitedDomain);
      return false;
    }
    this.isEmailUsed(emailValue, emailInput, errors);
  }

  isValidEmail(email: string): boolean {
    const emailRegexp = /[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}/gim;
    return emailRegexp.test(email);
  }

  isProhibitedDomain(email: string): boolean {
    const emailSplit = email.split("@");
    const emailDomain = emailSplit[1];
    return ProhibitedDomains.indexOf(emailDomain) > -1;
  }

  isEmailUsed(email: string, emailInput: HTMLInputElement, errors: any) {
    fetch(`/kontowyborcza/services/check-if-email-used?email=${email}&api=ajax2`)
      .then((response) => response.json())
      .then((data) => {
        if (data.used || data.usedGazeta) {
          this.displayEmailError(emailInput, errors.emailUsed);
        } else {
          this.formSubmit();
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  displayEmailError(emailInput: HTMLInputElement, text: string) {
    emailInput?.classList.add(ObjectConstants.error);
    const errorContainer = emailInput?.nextElementSibling as HTMLElement;
    const errorMessage = errorContainer?.querySelector(Selector.errorMessage) as HTMLElement;
    if (errorMessage) {
      errorMessage.textContent = text;
    }
  }

  validOneClickCondition(form: HTMLFormElement) {
    const oneClickElements = form.querySelectorAll(Selector.oneClickRequired);
    oneClickElements.forEach((element: Element) => {
      this.validCondition({
        form,
        type: Selector.oneClickRequired,
        unCheckCallback: (obj: Element) => {
          obj.removeAttribute(ObjectConstants.dataRequired);
        }
      });
    });
  }

  validRequiredCondition(form: HTMLFormElement) {
    this.validCondition({ form, type: ObjectConstants.requiredSelector });
  }

  validCondition(options: {
    form: HTMLFormElement;
    type: string;
    unCheckCallback?: (obj: HTMLInputElement, label: HTMLLabelElement) => void;
  }) {
    options.unCheckCallback = options.unCheckCallback || (() => {});

    const requiredConditions = options.form.querySelectorAll(`${ObjectConstants.checkboxConditions}${options.type}`);
    requiredConditions.forEach((obj) => {
      const input = obj as HTMLInputElement;
      this.removeInputError(input);
      if (!input.checked && input.getAttribute(ObjectConstants.dataRequired)) {
        this.addInputError(input);
        if (options.unCheckCallback) options.unCheckCallback(input, input.nextElementSibling as HTMLLabelElement);
      }
    });
  }

  addInputError(input: HTMLInputElement) {
    const label = input.nextElementSibling as HTMLLabelElement;
    label.classList.add(ObjectConstants.error);
  }

  removeInputError(input: HTMLInputElement) {
    const label = input.nextElementSibling as HTMLLabelElement;
    label.classList.remove(ObjectConstants.error);
  }

  formSubmit() {
    const errors = document.querySelectorAll(
      `${Selector.closeActivation} ${HtmlElements.form} ${Selector.error} `
    ) as NodeListOf<HTMLElement>;

    let utilizationData = this.collectUtilizationData();
    if (errors.length <= 0) {
      if (utilizationData) {
        this.sendBigDataUtilization();
        this.utilizationObject.utilize(utilizationData);
        this.sendFormData();
      } else {
        this.sendFormData();
      }
    }
  }

  addLazyLoader(element: HTMLElement) {
    const dimensions = element?.getBoundingClientRect(),
      scrollTop = document.body.scrollTop || document.documentElement.scrollTop,
      scrollLeft = document.body.scrollLeft || document.documentElement.scrollLeft,
      top = dimensions?.top + scrollTop,
      left = dimensions?.left + scrollLeft,
      width = dimensions?.width,
      height = dimensions?.height,
      loader = document.createElement("div");
    loader.classList.add(ObjectConstants.utilizationLoader);
    loader.style.cssText = `width: ${width}px; height: ${height}px; top: ${top}px; left: ${left}px;`;
    document.body.appendChild(loader);

    return {
      remove: () => {
        if (loader) {
          document.body.removeChild(loader);
        }
      }
    };
  }

  sendFormData() {
    const formData = this.getFormData();
    const dataString = this.appendDataToUrl(formData);
    this.fetchJSONP(formData.form?.action, dataString);
  }

  getFormData() {
    const form = this.module?.querySelector(HtmlElements.form) as HTMLFormElement;

    const customerProperties = form?.querySelectorAll(ObjectConstants.customerPropertiesSelector) as NodeListOf<
      HTMLInputElement | HTMLSelectElement
    >;
    const data = {
      form,
      customerProperties: {}
    };

    this.loader = this.addLazyLoader(form);

    customerProperties?.forEach((property) => {
      const customerProperty = property.getAttribute(ObjectConstants.customerPropertiesSelector);
      if (customerProperty) {
        data.customerProperties[customerProperty] = property.value;
      }
    });
    return data;
  }

  appendDataToUrl(data: { form: HTMLFormElement; customerProperties: object }) {
    const dataString = new URLSearchParams();

    for (const customerProperty in data.customerProperties) {
      dataString.append(`customerProperties[${customerProperty}]`, data.customerProperties[customerProperty]);
    }

    Array.from(data.form?.elements || []).forEach((input: Element) => {
      if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement) {
        if (input.name && input.value) {
          dataString.append(input.name, input.value);
        }
      }
    });

    return dataString.toString();
  }

  fetchJSONP(url: string, dataString: string) {
    const params = new URLSearchParams(dataString);

    const callbackName = "jsonpCallback";
    window[callbackName] = this.jsonpCallback.bind(this);
    const script = document.createElement("script");
    script.src = `${url}?callback=${callbackName}&${params}`;
    document.body.appendChild(script);
  }

  jsonpCallback(data: any) {
    const promoCodeError = this.module?.querySelector(Selector.promoCodeError);

    if (data.status === "ok") {
      location.reload();
    } else if (promoCodeError) {
      promoCodeError.classList.add(ObjectConstants.isVisible);
    }

    if (this.loader) {
      this.loader.remove();
    }
  }

  sendBigDataUtilization() {
    const parent = this.module?.closest(UtilizationConstants.classMcBan);
    const elementCtaCategory = parent?.querySelector(`[${CtaObjectConstants.dataCtaCategory}]`);

    if (elementCtaCategory) {
      BigData.sendDataOceanEvent(
        BigDataEventConstants.onClick,
        BigData.prepareDataOceanAttribute(this.ctaObject, this.collectionIdsObject)
      );
    }
  }

  collectUtilizationData(): any {
    let parent = this.module?.closest(UtilizationConstants.classMcBan),
      utilizeButton = this.module?.querySelector(`[${ObjectConstants.manualUtilizeAttr}]`),
      utilizationData;

    if (!parent || !utilizeButton) {
      return;
    }

    utilizationData = {
      campaignId: parent.getAttribute(UtilizationConstants.dataCampaignId),
      actionId: parent.getAttribute(UtilizationConstants.dataActionId),
      guid: BigDataHelper.getBigDataGUID(),
      condition: !!utilizeButton ? utilizeButton.getAttribute(ObjectConstants.manualUtilizeAttr) : ""
    };

    if (utilizationData.campaignId && utilizationData.actionId && utilizationData.guid && utilizationData.condition) {
      return utilizationData;
    } else {
      return null;
    }
  }

  getToken(tokenUrl: string) {
    if (window["squid"] === undefined) {
      const callbackName = "setTokenCallback";
      window[callbackName] = this.setToken.bind(this);
      const script = document.createElement("script");
      script.src = `${tokenUrl}?callback=${callbackName}`;
      script.async = true;
      document.body.appendChild(script);
    }
  }

  setToken(tokenObj: { value: string }) {
    const formcInput = DOMHelper.querySelector(ObjectConstants.formc);
    if (formcInput && (formcInput as HTMLInputElement)) {
      (formcInput as HTMLInputElement).value = tokenObj.value;
      this.token = tokenObj.value;
    }
  }
}
